import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import { adminPath } from '../../../shared/config/paths';

/** Yeap Access / Verify 后台 — /api/admin/access(团队、网关组/机器、公测设置)。 */
const { Text, Paragraph } = Typography;
const API = '/api/admin/access';
const data = (r) => (r?.data && 'data' in r.data ? r.data.data : r?.data);
const err = (e, fb = '失败') => { if (!e?.toasted) message.error(e?.response?.data?.error || e?.message || fb); };
const STAGE = { active: ['green', '正常'], grace: ['gold', '已过期(宽限)'], suspended: ['red', '已暂停'], disabled: ['default', '已停用'] };
const STATUS = { pending: ['gold', '开通中'], active: ['green', '正常'], failed: ['red', '开通失败'], deleted: ['default', '已删除'] };
const T = ({ map, v }) => { const [c, l] = map[v] || ['default', v]; return <Tag color={c}>{l}</Tag>; };

const Teams = ({ groups }) => {
    const [q, setQ] = useState('');
    const [rows, setRows] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        try { setRows(data(await apiClient.get(`${API}/teams`, { params: { q } }))); } catch (e) { err(e); } finally { setLoading(false); }
    }, [q]);
    useEffect(() => { load(); }, [load]);
    const save = async (v) => {
        try {
            await apiClient.put(`${API}/teams/${edit.id}`, {
                enterprise: v.enterprise, gateway_group_override_id: v.gateway_group_override_id || 0,
                egress_ips: (v.egress_ips || '').split(/[\s,]+/).filter(Boolean),
            });
            message.success('已保存,下一轮同步生效(≤1 分钟)');
            setEdit(null);
            load();
        } catch (e) { err(e); }
    };
    const prod = (r, p) => {
        const x = r.products[p];
        return <Space size={4}><Text>{x.users}/{x.seats}</Text><T map={STAGE} v={x.stage} />{x.plan ? <Tag>{x.plan.name}</Tag> : <Tag>免费</Tag>}</Space>;
    };
    return (
        <Card extra={<Space><Input.Search allowClear placeholder="slug / 名称 / 邮箱 / org id" onSearch={setQ} style={{ width: 280 }} /><Button icon={<ReloadOutlined />} onClick={load} /></Space>}>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows.items} pagination={{ total: rows.total, pageSize: 50 }}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 60 },
                    { title: '团队', render: (_, r) => <Space direction="vertical" size={0}><Text strong>{r.name}</Text><Text type="secondary">{r.slug} · 用户 {r.user_id} {r.email}</Text></Space> },
                    { title: '开通', render: (_, r) => <Space direction="vertical" size={0}><T map={STATUS} v={r.real_status} />{r.last_error && <Text type="danger" style={{ fontSize: 12 }}>{r.last_error}</Text>}</Space> },
                    { title: 'Access 人数', render: (_, r) => prod(r, 'access') },
                    { title: 'Verify 人数', render: (_, r) => prod(r, 'verify') },
                    { title: '设备', render: (_, r) => r.products.access.devices },
                    { title: '网关组', render: (_, r) => (groups.find((g) => g.id === r.gateway_group_id)?.name || '-') + (r.gateway_group_override_id ? '(专属)' : '') },
                    { title: '企业', render: (_, r) => (r.enterprise ? <Tag color="purple">企业</Tag> : '-') },
                    {
                        title: '', render: (_, r) => (
                            <Space>
                                <a href={`https://${r.dashboard_host}/access/ui/`} target="_blank" rel="noreferrer">后台</a>
                                <a onClick={() => setEdit(r)}>设置</a>
                                {r.real_status !== 'active' && <a onClick={() => apiClient.post(`${API}/teams/${r.id}/retry`).then(() => { message.success('已排队重试'); load(); }).catch(err)}>重试</a>}
                            </Space>
                        ),
                    },
                ]} />
            <Modal open={!!edit} title={`团队设置 · ${edit?.slug || ''}`} onCancel={() => setEdit(null)} footer={null} destroyOnHidden>
                {edit && (
                    <Form layout="vertical" onFinish={save} initialValues={{ enterprise: edit.enterprise, gateway_group_override_id: edit.gateway_group_override_id, egress_ips: (edit.egress_ips || []).join(' ') }}>
                        <Form.Item name="enterprise" label="企业(允许公共出口)" valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="egress_ips" label="出口 IP(空格分隔,公网)"><Input /></Form.Item>
                        <Form.Item name="gateway_group_override_id" label="专属网关组(空 = 按套餐)">
                            <Select allowClear options={groups.map((g) => ({ value: g.id, label: g.name + (g.dedicated ? '(专属)' : '') }))} />
                        </Form.Item>
                        <Button type="primary" htmlType="submit">保存</Button>
                    </Form>
                )}
            </Modal>
        </Card>
    );
};

const Gateways = ({ groups, machines, reload }) => {
    const [gEdit, setGEdit] = useState(null);
    const [mEdit, setMEdit] = useState(null);
    const [key, setKey] = useState(null);
    const saveGroup = async (v) => { try { await apiClient.post(`${API}/gateway-groups`, { ...v, id: gEdit.id }); setGEdit(null); reload(); } catch (e) { err(e); } };
    const saveMachine = async (v) => { try { await apiClient.post(`${API}/gateway-machines`, { ...v, id: mEdit.id }); setMEdit(null); reload(); } catch (e) { err(e); } };
    const register = async (m) => { try { setKey({ name: m.name, key: data(await apiClient.post(`${API}/gateway-machines/${m.id}/register`)).key }); reload(); } catch (e) { err(e); } };
    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Card title="网关组" extra={<Button onClick={() => setGEdit({ machine_ids: [] })}>新建组</Button>}>
                <Paragraph type="secondary">套餐默认组在产品 metadata.gateway_group_id 设置(空 = 默认组);续费保持当前组;专属组在团队设置里指定。</Paragraph>
                <Table rowKey="id" size="small" pagination={false} dataSource={groups} columns={[
                    { title: '组', render: (_, g) => <Space>{g.name}{!!g.is_default && <Tag color="blue">默认</Tag>}{!!g.dedicated && <Tag>专属</Tag>}</Space> },
                    { title: '机器', render: (_, g) => g.machine_ids.map((id) => machines.find((m) => m.id === id)?.name).filter(Boolean).join(', ') || '-' },
                    { title: '套餐', render: (_, g) => (g.plans || []).join(', ') || '-' },
                    { title: '团队数', dataIndex: 'teams' },
                    { title: '', render: (_, g) => <Space><a onClick={() => setGEdit(g)}>编辑</a><Popconfirm title="删除该组?" onConfirm={() => apiClient.delete(`${API}/gateway-groups/${g.id}`).then(reload).catch(err)}><a>删除</a></Popconfirm></Space> },
                ]} />
            </Card>
            <Card title="网关机器(第一批复用 CDN 节点)" extra={<Button onClick={() => setMEdit({ priority: 100, status: 'active' })}>添加机器</Button>}>
                <Table rowKey="id" size="small" pagination={false} dataSource={machines} columns={[
                    { title: '名称', dataIndex: 'name' }, { title: '地区', dataIndex: 'region' }, { title: '公网 IP', dataIndex: 'public_ip' },
                    { title: 'Endpoint', dataIndex: 'endpoint' }, { title: '优先级', dataIndex: 'priority' },
                    { title: '状态', render: (_, m) => <Space>{m.status === 'active' ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>}{m.remote_id ? <Tag color="blue">已注册</Tag> : <Tag color="gold">未注册</Tag>}</Space> },
                    { title: '', render: (_, m) => <Space><a onClick={() => setMEdit(m)}>编辑</a>{!m.remote_id && <a onClick={() => register(m)}>注册</a>}<Popconfirm title="删除机器?会从所有组织移除该网关" onConfirm={() => apiClient.delete(`${API}/gateway-machines/${m.id}`).then(reload).catch(err)}><a>删除</a></Popconfirm></Space> },
                ]} />
            </Card>
            <Modal open={!!gEdit} title="网关组" onCancel={() => setGEdit(null)} footer={null} destroyOnHidden>
                {gEdit && (
                    <Form layout="vertical" onFinish={saveGroup} initialValues={gEdit}>
                        <Form.Item name="name" label="组名" rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item name="is_default" label="默认组" valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="dedicated" label="专属(只给指定团队)" valuePropName="checked"><Switch /></Form.Item>
                        <Form.Item name="machine_ids" label="机器"><Select mode="multiple" options={machines.map((m) => ({ value: m.id, label: `${m.name} (${m.region})` }))} /></Form.Item>
                        <Form.Item name="notes" label="备注"><Input /></Form.Item>
                        <Button type="primary" htmlType="submit">保存</Button>
                    </Form>
                )}
            </Modal>
            <Modal open={!!mEdit} title="网关机器" onCancel={() => setMEdit(null)} footer={null} destroyOnHidden>
                {mEdit && (
                    <Form layout="vertical" onFinish={saveMachine} initialValues={mEdit}>
                        <Form.Item name="name" label="名称(小写字母数字-)" rules={[{ required: true }]}><Input placeholder="lax-1" /></Form.Item>
                        <Form.Item name="region" label="地区"><Input placeholder="us-west" /></Form.Item>
                        <Form.Item name="public_ip" label="公网 IP" rules={[{ required: true }]}><Input /></Form.Item>
                        <Form.Item name="endpoint" label="Endpoint(host:port,WireGuard UDP)" rules={[{ required: true }]}><Input placeholder="203.0.113.10:51820" /></Form.Item>
                        <Form.Item name="priority" label="优先级(小的优先)"><InputNumber /></Form.Item>
                        <Form.Item name="cdn_node_id" label="CDN 节点 ID(复用时)"><InputNumber /></Form.Item>
                        <Form.Item name="status" label="状态"><Select options={[{ value: 'active', label: '启用' }, { value: 'disabled', label: '停用' }]} /></Form.Item>
                        <Button type="primary" htmlType="submit">保存</Button>
                    </Form>
                )}
            </Modal>
            <Modal open={!!key} title={`机器密钥 · ${key?.name || ''}`} onCancel={() => setKey(null)} onOk={() => setKey(null)}>
                <Alert type="warning" showIcon message="只显示这一次,写入网关节点的配置(见 api/docs/ACCESS_DEPLOY.md)" />
                <Paragraph copyable code style={{ marginTop: 12 }}>{key?.key}</Paragraph>
            </Modal>
        </Space>
    );
};

const Settings = () => {
    const [form] = Form.useForm();
    const [s, setS] = useState(null);
    const load = useCallback(() => apiClient.get(`${API}/settings`).then((r) => { const d = data(r); setS(d); form.setFieldsValue(d); }).catch(err), [form]);
    useEffect(() => { load(); }, [load]);
    const save = async (v) => { try { await apiClient.put(`${API}/settings`, v); message.success('已保存'); load(); } catch (e) { err(e); } };
    return (
        <Card>
            <Form form={form} layout="vertical" onFinish={save} style={{ maxWidth: 420 }}>
                <Form.Item name="access_sales_enabled" label="公开测试版开放开通" valuePropName="checked"><Switch /></Form.Item>
                <Form.Item name="access_suspend_after_days" label="过期满几天暂停(员工无法连接/验证)"><InputNumber min={0} max={90} /></Form.Item>
                <Form.Item name="access_disable_after_days" label="过期满几天停用(数据保留)"><InputNumber min={0} max={90} /></Form.Item>
                <Paragraph type="secondary">免费人数:Access {s?.free_seats?.access} / Verify {s?.free_seats?.verify}。{s?.note}</Paragraph>
                <Button type="primary" htmlType="submit">保存</Button>
            </Form>
        </Card>
    );
};

const AccessAdmin = () => {
    const { tab = 'teams' } = useParams();
    const navigate = useNavigate();
    const [gw, setGw] = useState({ items: [], machines: [] });
    const reload = useCallback(() => apiClient.get(`${API}/gateway-groups`).then((r) => setGw(data(r))).catch(err), []);
    useEffect(() => { reload(); }, [reload]);
    return (
        <Tabs activeKey={tab} onChange={(k) => navigate(adminPath(`/access/${k}`))} items={[
            { key: 'teams', label: '团队', children: <Teams groups={gw.items} /> },
            { key: 'gateways', label: '网关组', children: <Gateways groups={gw.items} machines={gw.machines} reload={reload} /> },
            { key: 'settings', label: '设置', children: <Settings /> },
        ]} />
    );
};

export default AccessAdmin;
